<template>
  <v-container fluid class="pa-0">
    <!-- Header with actions only -->
    <!-- ✅ ALTERNATIVE: Ultra-compact header with tooltips -->
    <v-card class="mb-4">
      <v-card-title class="py-2">
        <div class="d-flex align-center justify-space-between w-100">
          <!-- Left side: Title and filters -->
          <div class="d-flex align-center">
            <h3 class="mr-3">Athlete Pipeline</h3>
            
            <!-- Compact filters -->
            <div v-if="selectedEvent.length > 0 || search" class="d-flex align-center">
              <v-chip 
                v-if="selectedEvent.length > 0" 
                x-small 
                color="primary" 
                class="mr-1"
              >
                <v-icon x-small class="mr-1">event</v-icon>
                {{ selectedEvent.length }}
              </v-chip>
              <v-chip 
                v-if="search" 
                x-small 
                color="primary" 
                class="mr-2"
              >
                <v-icon x-small class="mr-1">search</v-icon>
                {{ search.length > 10 ? search.substring(0, 10) + '...' : search }}
              </v-chip>
            </div>
          </div>

          <!-- Right side: Compact controls -->
          <div class="d-flex align-center">
            <!-- Column info with tooltip -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span 
                  class="text-caption mr-2" 
                  v-bind="attrs" 
                  v-on="on"
                >
                  {{ visibleKanbanStatuses.length }}/{{ kanbanStatuses.length }}
                  <span v-if="hiddenColumns.length > 0">
                    ({{ hiddenColumns.length }} hidden)
                  </span>
                </span>
              </template>
              <span>{{ visibleKanbanStatuses.length }} visible columns out of {{ kanbanStatuses.length }} total</span>
            </v-tooltip>
            
            <!-- ✅ ADD: Swimlane toggle -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  :color="showSwimlanes ? 'primary' : 'default'"
                  @click="toggleSwimlanes"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-1"
                >
                  <v-icon small>view_stream</v-icon>
                </v-btn>
              </template>
              <span>{{ showSwimlanes ? 'Hide' : 'Show' }} Event Swimlanes</span>
            </v-tooltip>
            
            <!-- ✅ ADD: Event filter (only when swimlanes are enabled) -->
            <div v-if="showSwimlanes" class="mr-2">
              <EventCatalogSelect
                v-model="selectedSwimlaneEvent"
                :clubId="$route.params.club"
                dense
                clearable
                style="max-width: 120px;"
                placeholder="All Events"
              />
            </div>
            
            <!-- View toggle -->
            <v-btn-toggle v-model="viewMode" mandatory class="mr-2" dense>
              <v-btn x-small value="compact">
                <v-icon x-small>view_column</v-icon>
              </v-btn>
              <v-btn x-small value="comfortable">
                <v-icon x-small>view_comfortable</v-icon>
              </v-btn>
            </v-btn-toggle>
            
            <!-- Action buttons with tooltips -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  @click="openStatusOrderDialog"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-1"
                >
                  <v-icon small>settings</v-icon>
                </v-btn>
              </template>
              <span>Settings</span>
            </v-tooltip>
            
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  @click="openAddAthleteDialog"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-1"
                >
                  <v-icon small>person_add</v-icon>
                </v-btn>
              </template>
              <span>Add Athlete</span>
            </v-tooltip>
            
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="secondary"
                  @click="refreshData"
                  :loading="isLoading"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>refresh</v-icon>
                </v-btn>
              </template>
              <span>Refresh Data</span>
            </v-tooltip>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- Kanban Board -->
    <div class="kanban-container">
      <!-- ✅ REGULAR VIEW: Single row of columns -->
      <div v-if="!showSwimlanes" class="kanban-board" :class="viewModeClass">
        <div 
          v-for="status in visibleKanbanStatuses" 
          :key="status.value"
          class="kanban-column"
          :class="`column-${status.value}`"
        >
          <!-- Column Header -->
          <v-card class="kanban-column-header" elevation="2">
            <v-card-title class="py-2 px-3">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center flex-grow-1">
                  <v-icon 
                    :color="status.color" 
                    class="mr-2"
                    :size="viewMode === 'compact' ? 16 : 20"
                  >
                    {{ status.icon }}
                  </v-icon>
                  <span 
                    :class="viewMode === 'compact' ? 'text-body-2 font-weight-bold' : 'text-h6 font-weight-bold'"
                    class="status-title"
                  >
                    {{ status.text }}
                  </span>
                </div>
                <div class="d-flex align-center">
                  <v-chip 
                    :color="status.color" 
                    text-color="white" 
                    :small="viewMode === 'compact'"
                    class="mr-2"
                  >
                    {{ getAthletesByStatus(status.value).length }}
                  </v-chip>
                  
                  <v-btn 
                    icon 
                    :small="viewMode === 'compact'"
                    @click="hideColumn(status.value)"
                    :title="'Hide ' + status.text"
                  >
                    <v-icon :small="viewMode === 'compact'">visibility_off</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-title>
          </v-card>

          <!-- Droppable Column Area -->
          <div 
            class="kanban-column-content"
            @drop="onDrop($event, status.value)"
            @dragover.prevent
            @dragenter.prevent
          >
            <!-- Athlete Cards -->
            <div
              v-for="athlete in getAthletesByStatus(status.value)"
              :key="athlete.seqNo"
              class="athlete-card"
              draggable="true"
              @dragstart="onDragStart($event, athlete)"
            >
              <v-card 
                :class="getCardClass(athlete)"
                @click="openAthleteDetails(athlete)"
                elevation="1"
                hover
              >
                <!-- ✅ COMPACT: Ultra-minimal card content -->
                <v-card-text v-if="viewMode === 'compact'" class="py-1 px-2">
                  <div class="d-flex align-center justify-space-between">
                    <!-- Name only -->
                    <div class="athlete-name-compact">
                      {{ athlete.person.fullName }}
                    </div>
                    
                    <!-- Age + menu -->
                    <div class="d-flex align-center">
                      <v-chip 
                        x-small 
                        :color="getAgeColor(athlete)"
                        text-color="white"
                        class="compact-chip mr-1"
                      >
                        {{ getDaysFromUpdate(athlete) }}d
                      </v-chip>
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            x-small
                            v-bind="attrs"
                            v-on="on"
                            @click.stop
                            class="compact-menu-btn"
                          >
                            <v-icon class="compact-icon">more_vert</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="editAthlete(athlete)">
                            <v-list-item-icon><v-icon small>edit</v-icon></v-list-item-icon>
                            <v-list-item-title>Edit</v-list-item-title>
                          </v-list-item>
                          <v-list-item 
                            @click="sendEmail(athlete.person && athlete.person.email)" 
                            :disabled="!(athlete.person && athlete.person.email)"
                          >
                            <v-list-item-icon><v-icon small>email</v-icon></v-list-item-icon>
                            <v-list-item-title>Email</v-list-item-title>
                          </v-list-item>
                          <v-list-item 
                            @click="callPhone(athlete.person && athlete.person.phone)"
                            :disabled="!(athlete.person && athlete.person.phone)"
                          >
                            <v-list-item-icon><v-icon small>phone</v-icon></v-list-item-icon>
                            <v-list-item-title>Call</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </div>
                  
                  <!-- Essential info in one line -->
                  <div class="compact-info mt-1">
                    <span class="text-caption-compact">
                      {{ (athlete.person && athlete.person.email) || 'No email' }}
                    </span>
                  </div>
                  
                  <!-- Communication indicator only if exists -->
                  <div v-if="getCommunicationCount(athlete.additionalInfo) > 0" class="mt-1">
                    <v-chip 
                      x-small 
                      color="info" 
                      text-color="white"
                      @click.stop="showCommunications(athlete)"
                      class="compact-chip"
                    >
                      <v-icon x-small class="mr-1">chat</v-icon>
                      {{ getCommunicationCount(athlete.additionalInfo) }}
                    </v-chip>
                  </div>
                </v-card-text>

                <!-- ✅ COMFORTABLE: Full info (your existing card content) -->
                <v-card-text v-else class="py-2">
                  <!-- Athlete Header -->
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="athlete-name">
                      <strong>{{ athlete.person.fullName }}</strong>
                    </div>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          v-bind="attrs"
                          v-on="on"
                          @click.stop
                        >
                          <v-icon>more_vert</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item @click="editAthlete(athlete)">
                          <v-list-item-icon><v-icon>edit</v-icon></v-list-item-icon>
                          <v-list-item-title>Edit</v-list-item-title>
                        </v-list-item>
                        <v-list-item 
                          @click="sendEmail(athlete.person && athlete.person.email)" 
                          :disabled="!(athlete.person && athlete.person.email)"
                        >
                          <v-list-item-icon><v-icon>email</v-icon></v-list-item-icon>
                          <v-list-item-title>Email</v-list-item-title>
                        </v-list-item>
                        <v-list-item 
                          @click="callPhone(athlete.person && athlete.person.phone)"
                          :disabled="!(athlete.person && athlete.person.phone)"
                        >
                          <v-list-item-icon><v-icon>phone</v-icon></v-list-item-icon>
                          <v-list-item-title>Call</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>

                  <!-- Athlete Info -->
                  <div class="athlete-info">
                    <div class="text-caption mb-1">
                      <v-icon x-small class="mr-1">event</v-icon>
                      {{ (athlete.event && athlete.event.name) || 'No Event' }}
                    </div>
                    
                    <div class="text-caption mb-1">
                      <v-icon x-small class="mr-1">group</v-icon>
                      {{ (athlete.group && athlete.group.teamName) || 'No Group' }}
                    </div>
                    
                    <div class="text-caption mb-1" v-if="athlete.tryoutDatetime">
                      <v-icon x-small class="mr-1">schedule</v-icon>
                      {{ formatDatetime(athlete.tryoutDatetime) }}
                    </div>
                    
                    <div class="text-caption mb-1">
                      <v-icon x-small class="mr-1">email</v-icon>
                      {{ (athlete.person && athlete.person.email) || 'No email' }}
                    </div>
                    
                    <div class="text-caption" v-if="athlete.person && athlete.person.phone">
                      <v-icon x-small class="mr-1">phone</v-icon>
                      {{ athlete.person.phone }}
                    </div>
                  </div>

                  <!-- Communication Summary -->
                  <div v-if="getCommunicationCount(athlete.additionalInfo) > 0" class="mt-2">
                    <v-chip 
                      x-small 
                      color="info" 
                      text-color="white"
                      @click.stop="showCommunications(athlete)"
                    >
                      <v-icon x-small class="mr-1">chat</v-icon>
                      {{ getCommunicationCount(athlete.additionalInfo) }} comms
                    </v-chip>
                  </div>

                  <!-- Age indicator -->
                  <div class="mt-2">
                    <v-chip 
                      x-small 
                      :color="getAgeColor(athlete)"
                      text-color="white"
                    >
                      {{ getDaysFromUpdate(athlete) }} days
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Empty State -->
            <div v-if="getAthletesByStatus(status.value).length === 0" class="empty-column">
              <v-icon color="grey lighten-2" size="48">{{ status.icon }}</v-icon>
              <div class="text-center text--secondary mt-2">
                No athletes in {{ status.text.toLowerCase() }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ✅ NEW: SWIMLANE VIEW - Proper Matrix Layout -->
      <div v-else class="swimlane-container" :class="viewModeClass">
        <!-- ✅ STICKY STATUS HEADERS (Always visible at top) -->
        <div class="swimlane-sticky-header">
          <div 
            v-for="status in visibleKanbanStatuses" 
            :key="`sticky-${status.value}`"
            class="swimlane-sticky-column"
          >
            <v-card class="sticky-status-header" elevation="3">
              <v-card-title class="py-2 px-3">
                <div class="d-flex align-center justify-space-between w-100">
                  <div class="d-flex align-center">
                    <v-icon 
                      :color="status.color" 
                      class="mr-2"
                      size="18"
                    >
                      {{ status.icon }}
                    </v-icon>
                    <span class="text-body-2 font-weight-bold status-title">
                      {{ status.text }}
                    </span>
                  </div>
                  <v-chip 
                    :color="status.color" 
                    text-color="white" 
                    x-small
                  >
                    {{ getAthletesByStatus(status.value).length }}
                  </v-chip>
                </div>
              </v-card-title>
            </v-card>
          </div>
        </div>

        <!-- ✅ SCROLLABLE SWIMLANE CONTENT -->
        <div class="swimlane-content">
          <div 
            v-for="event in filteredSwimlaneEvents" 
            :key="event.eventId"
            class="swimlane-event-row"
          >
            <!-- ✅ EVENT HEADER ROW (spans all columns) -->
            <div class="event-header-row">
              <v-card class="event-banner" :color="event.color" dark elevation="1">
                <v-card-title class="py-1 px-3">
                  <div class="d-flex align-center justify-space-between w-100">
                    <div class="d-flex align-center">
                      <v-icon class="mr-2" size="16">event</v-icon>
                      <span class="text-body-2 font-weight-bold event-title">
                        {{ event.name }}
                      </span>
                    </div>
                    <div class="d-flex align-center">
                      <v-chip 
                        :color="event.color" 
                        :text-color="event.color === 'yellow' ? 'black' : 'white'"
                        x-small
                        outlined
                        class="mr-2"
                      >
                        {{ getTotalAthletesInSwimlane(event.eventId) }} athletes
                      </v-chip>
                      
                      <!-- ✅ Event actions -->
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            x-small
                            v-bind="attrs"
                            v-on="on"
                            @click.stop
                          >
                            <v-icon small>more_vert</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="focusOnEvent(event.eventId)">
                            <v-list-item-icon><v-icon small>center_focus_strong</v-icon></v-list-item-icon>
                            <v-list-item-title>Focus on this event</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="exportEventData(event.eventId)">
                            <v-list-item-icon><v-icon small>download</v-icon></v-list-item-icon>
                            <v-list-item-title>Export data</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </div>
                </v-card-title>
              </v-card>
            </div>

            <!-- ✅ STATUS COLUMNS ROW (aligned with sticky headers) -->
            <div class="status-columns-row">
              <div 
                v-for="status in visibleKanbanStatuses" 
                :key="`${event.eventId}-${status.value}`"
                class="swimlane-status-column"
              >
                <!-- ✅ Droppable area for this event+status combination -->
                <div 
                  class="swimlane-drop-zone"
                  @drop="onDrop($event, status.value)"
                  @dragover.prevent
                  @dragenter.prevent
                  @dragenter="onDragEnter($event)"
                  @dragleave="onDragLeave($event)"
                >
                  <!-- ✅ Athletes in this event+status - RESPECT VIEW MODE -->
                  <div
                    v-for="athlete in getAthletesByEventAndStatus(event.eventId, status.value)"
                    :key="athlete.seqNo"
                    class="swimlane-athlete-card"
                    draggable="true"
                    @dragstart="onDragStart($event, athlete)"
                  >
                    <v-card 
                      :class="getCardClass(athlete)"
                      @click="openAthleteDetails(athlete)"
                      elevation="1"
                      hover
                      class="compact-athlete-card"
                    >
                      <!-- ✅ COMPACT VIEW in swimlanes -->
                      <v-card-text v-if="viewMode === 'compact'" class="py-1 px-2">
                        <div class="d-flex align-center justify-space-between">
                          <!-- Name only -->
                          <div class="athlete-name-compact">
                            {{ athlete.person.fullName }}
                          </div>
                          
                          <!-- Age + menu -->
                          <div class="d-flex align-center">
                            <v-chip 
                              x-small 
                              :color="getAgeColor(athlete)"
                              text-color="white"
                              class="compact-chip mr-1"
                            >
                              {{ getDaysFromUpdate(athlete) }}d
                            </v-chip>
                            <v-menu offset-y>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  x-small
                                  v-bind="attrs"
                                  v-on="on"
                                  @click.stop
                                  class="compact-menu-btn"
                                >
                                  <v-icon class="compact-icon">more_vert</v-icon>
                                </v-btn>
                              </template>
                              <v-list dense>
                                <v-list-item @click="editAthlete(athlete)">
                                  <v-list-item-icon><v-icon small>edit</v-icon></v-list-item-icon>
                                  <v-list-item-title>Edit</v-list-item-title>
                                </v-list-item>
                                <v-list-item 
                                  @click="sendEmail(athlete.person && athlete.person.email)" 
                                  :disabled="!(athlete.person && athlete.person.email)"
                                >
                                  <v-list-item-icon><v-icon small>email</v-icon></v-list-item-icon>
                                  <v-list-item-title>Email</v-list-item-title>
                                </v-list-item>
                                <v-list-item 
                                  @click="callPhone(athlete.person && athlete.person.phone)"
                                  :disabled="!(athlete.person && athlete.person.phone)"
                                >
                                  <v-list-item-icon><v-icon small>phone</v-icon></v-list-item-icon>
                                  <v-list-item-title>Call</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                        </div>
                        
                        <!-- Essential info in one line -->
                        <div class="compact-info mt-1">
                          <span class="text-caption-compact">
                            {{ (athlete.person && athlete.person.email) || 'No email' }}
                          </span>
                        </div>
                        
                        <!-- Communication indicator only if exists -->
                        <div v-if="getCommunicationCount(athlete.additionalInfo) > 0" class="mt-1">
                          <v-chip 
                            x-small 
                            color="info" 
                            text-color="white"
                            @click.stop="showCommunications(athlete)"
                            class="compact-chip"
                          >
                            <v-icon x-small class="mr-1">chat</v-icon>
                            {{ getCommunicationCount(athlete.additionalInfo) }}
                          </v-chip>
                        </div>
                      </v-card-text>

                      <!-- ✅ COMFORTABLE VIEW in swimlanes -->
                      <v-card-text v-else class="py-2">
                        <!-- Athlete Header -->
                        <div class="d-flex align-center justify-space-between mb-2">
                          <div class="athlete-name">
                            <strong>{{ athlete.person.fullName }}</strong>
                          </div>
                          <v-menu offset-y>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                x-small
                                v-bind="attrs"
                                v-on="on"
                                @click.stop
                              >
                                <v-icon>more_vert</v-icon>
                              </v-btn>
                            </template>
                            <v-list dense>
                              <v-list-item @click="editAthlete(athlete)">
                                <v-list-item-icon><v-icon>edit</v-icon></v-list-item-icon>
                                <v-list-item-title>Edit</v-list-item-title>
                              </v-list-item>
                              <v-list-item 
                                @click="sendEmail(athlete.person && athlete.person.email)" 
                                :disabled="!(athlete.person && athlete.person.email)"
                              >
                                <v-list-item-icon><v-icon>email</v-icon></v-list-item-icon>
                                <v-list-item-title>Email</v-list-item-title>
                              </v-list-item>
                              <v-list-item 
                                @click="callPhone(athlete.person && athlete.person.phone)"
                                :disabled="!(athlete.person && athlete.person.phone)"
                              >
                                <v-list-item-icon><v-icon>phone</v-icon></v-list-item-icon>
                                <v-list-item-title>Call</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </div>

                        <!-- Athlete Info -->
                        <div class="athlete-info">
                          <div class="text-caption mb-1">
                            <v-icon x-small class="mr-1">event</v-icon>
                            {{ (athlete.event && athlete.event.name) || 'No Event' }}
                          </div>
                          
                          <div class="text-caption mb-1">
                            <v-icon x-small class="mr-1">group</v-icon>
                            {{ (athlete.group && athlete.group.teamName) || 'No Group' }}
                          </div>
                          
                          <div class="text-caption mb-1" v-if="athlete.tryoutDatetime">
                            <v-icon x-small class="mr-1">schedule</v-icon>
                            {{ formatDatetime(athlete.tryoutDatetime) }}
                          </div>
                          
                          <div class="text-caption mb-1">
                            <v-icon x-small class="mr-1">email</v-icon>
                            {{ (athlete.person && athlete.person.email) || 'No email' }}
                          </div>
                          
                          <div class="text-caption" v-if="athlete.person && athlete.person.phone">
                            <v-icon x-small class="mr-1">phone</v-icon>
                            {{ athlete.person.phone }}
                          </div>
                        </div>

                        <!-- Communication Summary -->
                        <div v-if="getCommunicationCount(athlete.additionalInfo) > 0" class="mt-2">
                          <v-chip 
                            x-small 
                            color="info" 
                            text-color="white"
                            @click.stop="showCommunications(athlete)"
                          >
                            <v-icon x-small class="mr-1">chat</v-icon>
                            {{ getCommunicationCount(athlete.additionalInfo) }} comms
                          </v-chip>
                        </div>

                        <!-- Age indicator -->
                        <div class="mt-2">
                          <v-chip 
                            x-small 
                            :color="getAgeColor(athlete)"
                            text-color="white"
                          >
                            {{ getDaysFromUpdate(athlete) }} days
                          </v-chip>
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>

                  <!-- ✅ Empty state indicator -->
                  <div 
                    v-if="getAthletesByEventAndStatus(event.eventId, status.value).length === 0" 
                    class="empty-drop-zone"
                  >
                    <v-icon color="grey lighten-3" size="12">{{ status.icon }}</v-icon>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ✅ Empty State when no events -->
        <div v-if="filteredSwimlaneEvents.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey lighten-2">event_busy</v-icon>
          <div class="text-h6 text--secondary mt-2">No events found</div>
          <div class="text-body-2 text--secondary">Athletes need to be assigned to events to show swimlanes</div>
        </div>
      </div>
    </div>

    <!-- ✅ FIX: Status Order Management Dialog -->
    <v-dialog v-model="showStatusOrderDialog" max-width="600px">
      <v-card>
        <v-card-title>
          Manage Columns
          <v-spacer />
          <v-btn icon @click="showStatusOrderDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <!-- ✅ ADD: Tabs for order and visibility -->
          <v-tabs v-model="managementTab">
            <v-tab>Column Order</v-tab>
            <v-tab>Column Visibility</v-tab>
          </v-tabs>
          
          <v-tabs-items v-model="managementTab">
            <!-- Order Management Tab -->
            <v-tab-item>
              <div class="pa-4">
                <div class="text-body-2 mb-4">
                  Drag and drop to reorder the kanban columns:
                </div>
                
                <draggable 
                  v-model="statusOrderList" 
                  @end="onStatusOrderDragEnd"
                  handle=".order-drag-handle"
                  :animation="200"
                >
                  <v-card 
                    v-for="(status, index) in statusOrderList" 
                    :key="status.value"
                    outlined
                    class="mb-2 pa-2"
                  >
                    <div class="d-flex align-center">
                      <v-icon class="order-drag-handle mr-3" style="cursor: grab;">
                        drag_indicator
                      </v-icon>
                      <v-icon :color="status.color" class="mr-2">
                        {{ status.icon }}
                      </v-icon>
                      <span class="flex-grow-1">{{ status.text }}</span>
                      <v-chip small :color="status.color" text-color="white">
                        {{ index + 1 }}
                      </v-chip>
                    </div>
                  </v-card>
                </draggable>
                
                <v-divider class="my-4"></v-divider>
                <div class="text-caption text--secondary">
                  Current order: {{ statusOrderList.map(s => s.text).join(' → ') }}
                </div>
              </div>
            </v-tab-item>
            
            <!-- Visibility Management Tab -->
            <v-tab-item>
              <div class="pa-4">
                <div class="text-body-2 mb-4">
                  Show or hide columns in your kanban board:
                </div>
                
                <v-row dense>
                  <v-col 
                    v-for="status in orderedKanbanStatuses" 
                    :key="status.value"
                    cols="12"
                  >
                    <v-card 
                      outlined 
                      class="pa-2"
                      :class="{ 'opacity-50': hiddenColumns.includes(status.value) }"
                    >
                      <div class="d-flex align-center justify-space-between">
                        <div class="d-flex align-center">
                          <v-icon :color="status.color" class="mr-2">
                            {{ status.icon }}
                          </v-icon>
                          <span class="flex-grow-1">{{ status.text }}</span>
                          <v-chip 
                            x-small 
                            :color="status.color" 
                            text-color="white"
                            class="ml-2"
                          >
                            {{ getAthletesByStatus(status.value).length }}
                          </v-chip>
                        </div>
                        
                        <v-switch
                          :input-value="!hiddenColumns.includes(status.value)"
                          @change="toggleColumnVisibility(status.value)"
                          color="primary"
                          dense
                          hide-details
                        />
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                
                <v-divider class="my-4"></v-divider>
                <div class="d-flex justify-space-between">
                  <v-btn 
                    text 
                    small 
                    @click="showAllColumns"
                    :disabled="hiddenColumns.length === 0"
                  >
                    <v-icon small class="mr-1">visibility</v-icon>
                    Show All
                  </v-btn>
                  <v-btn 
                    text 
                    small 
                    @click="hideEmptyColumns"
                  >
                    <v-icon small class="mr-1">visibility_off</v-icon>
                    Hide Empty
                  </v-btn>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        
        <v-card-actions>
          <v-btn text @click="resetStatusOrder">
            <v-icon small class="mr-1">restore</v-icon>
            Reset to Default
          </v-btn>
          <v-spacer />
          <v-btn text @click="showStatusOrderDialog = false">Close</v-btn>
          <v-btn color="primary" @click="saveStatusOrder">
            <v-icon small class="mr-1">save</v-icon>
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Communications Dialog -->
    <v-dialog v-model="communicationsDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <!-- ✅ FIX: Vue 2 compatible null check -->
          Communications - {{ (selectedAthlete && selectedAthlete.person && selectedAthlete.person.fullName) || 'Unknown' }}
          <v-spacer />
          <v-btn icon @click="communicationsDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <!-- ✅ FIX: Vue 2 compatible null check -->
          <div v-if="selectedAthlete" v-html="getLatestCommunication(selectedAthlete.additionalInfo)"></div>
          <div v-else class="text-center text--secondary">No athlete selected</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ✅ ADD: PersonDialog -->
    <PersonDialog
      v-model="dialog"
      :editedItem="editedItem"
      :editedIndex="editedIndex"
      :groupListFiltered="groupListForDialog"
      :loading="isLoading"
      @close="close"
      @save="saveAthlete"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import draggable from 'vuedraggable';
import {
  formatDatetime,
  getCommunicationCount,
  getLatestCommunication,
} from "../../../_helpers/index";
import EventCatalogSelect from "../../shared/selectEventCatalog.vue";
import { getCatValues } from "../../../mixins/getCatValues.js";
// ✅ ADD: Import PersonDialog
import PersonDialog from "./PersonDialog.vue";

export default {
  name: "AthleteKanban",
  
  components: {
    EventCatalogSelect,
    draggable,
    PersonDialog, // ✅ ADD: Register PersonDialog component
  },
  
  mixins: [getCatValues],
  
  props: {
    statusSelected: {
      type: Array,
      default: () => []
    },
    search: {
      type: String,
      default: ""
    },
    selectedEvent: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      isLoading: false, // ✅ Changed from 'loading' to 'isLoading'
      communicationsDialog: false,
      selectedAthlete: null,
      draggedAthlete: null,

      // ✅ ADD: Dialog properties
      dialog: false,
      editedIndex: -1,
      editedItem: {
        person: {
          personId: 0,
          firstName: "",
          lastName: "",
          birthDate: "",
          photo: "",
          gender: 0,
          email: "",
          phone: "",
          registrationState: false,
          status: 0,
        },
        status: "active",
        additionalInfo: "",
        participationDays: "",
        tryoutDate: null,
        tryoutTime: null,
        volunteerPreference: 0,
        teamId: 0,
        clubId: 0,
      },
      defaultItem: {
        person: {
          personId: 0,
          firstName: "",
          lastName: "",
          birthDate: "",
          photo: "",
          gender: 0,
          email: "",
          phone: "",
          registrationState: false,
          status: 0,
        },
        status: "active",
        additionalInfo: "",
        participationDays: "",
        tryoutDate: null,
        tryoutTime: null,
        volunteerPreference: 0,
        teamId: 0,
        clubId: 0,
      },
      
      // Status order and visibility management
      showStatusOrderDialog: false,
      statusOrderList: [],
      customStatusOrder: [],
      hiddenColumns: [],
      managementTab: 0,
      
      // View mode
      viewMode: 'comfortable', // 'compact' or 'comfortable'
      
      // Default status order (fallback)
defaultStatusOrder: [
      'applied',      // 1. Start of funnel
      'waitlist',     // 2. In consideration
      'test',         // 3. Testing phase
      'approved',     // 4. Approved
      'deposit',      // 5. Payment made
      'active',       // 6. Active member
      'complete',     // 7. Completed program
      'alumni',       // 8. Alumni
      'inactive',     // 9. Inactive
      'reconnect',    // 10. Trying to reconnect
      'declined'      // 11. End of funnel
    ],
      
      // ✅ UPDATE: Icon mapping for your actual statuses
      statusIcons: {
        'active': 'check_circle',
        'inactive': 'pause_circle_outline',
        'waitlist': 'schedule',
        'applied': 'assignment_turned_in',
        'deposit': 'payment',
        'declined': 'cancel',
        'reconnect': 'refresh',
        'test': 'science',
        'approved': 'verified',
        'alumni': 'school',
        'complete': 'done_all'
      },
      
      // ✅ UPDATE: Color mapping for your actual statuses
      statusColors: {
        'active': 'success',
        'inactive': 'grey',
        'waitlist': 'orange',
        'applied': 'blue',
        'deposit': 'green',
        'declined': 'red',
        'reconnect': 'purple',
        'test': 'teal',
        'approved': 'light-green',
        'alumni': 'indigo',
        'complete': 'cyan'
      },

      
      // ✅ ADD: Management tab for dialog
      managementTab: 0,

      // ✅ ADD: Swimlane controls
      showSwimlanes: false, // Toggle for swimlane view
      selectedSwimlaneEvent: null, // Filter to specific event
    };
  },
  
  computed: {
    ...mapGetters([
      "clubPersonRel",
      "user",
    ]),
    
    // // ✅ ADD: View mode class
    // viewModeClass() {
    //   return `view-${this.viewMode}`;
    // },
    
    // ✅ ADD: Get kanban statuses from category values
    kanbanStatuses() {
      if (!this.catValueListArray) return [];
      
      return [...this.catValueListArray("athlete_status")].map(status => {
        // Handle both string and object formats
        const statusValue = typeof status === 'string' ? status : status.value;
        const statusText = typeof status === 'string' ? status : (status.text || status.label || status.value);
        
        return {
          value: statusValue,
          text: statusText,
          color: this.statusColors[statusValue.toLowerCase()] || 'grey',
          icon: this.statusIcons[statusValue.toLowerCase()] || 'circle'
        };
      });
    },
    
    // ✅ FIX: Ordered kanban statuses that actually updates
    orderedKanbanStatuses() {
      if (!this.kanbanStatuses.length) return [];
      
      // Use custom order if available, otherwise use default
      const orderToUse = this.customStatusOrder.length > 0 
        ? this.customStatusOrder 
        : this.defaultStatusOrder;
      
      // Create a map for quick lookup
      const statusMap = {};
      this.kanbanStatuses.forEach(status => {
        statusMap[status.value.toLowerCase()] = status;
      });
      
      const orderedStatuses = [];
      
      // Add statuses in the specified order
      orderToUse.forEach(statusValue => {
        const status = statusMap[statusValue.toLowerCase()];
        if (status) {
          orderedStatuses.push(status);
          delete statusMap[statusValue.toLowerCase()]; // Remove from map
        }
      });
      
      // Add any remaining statuses that weren't in the order list
      Object.values(statusMap).forEach(status => {
        orderedStatuses.push(status);
      });
      
      return orderedStatuses;
    },
    
    // ✅ ADD: Visible kanban statuses (excluding hidden ones)
    visibleKanbanStatuses() {
      return this.orderedKanbanStatuses.filter(status => 
        !this.hiddenColumns.includes(status.value)
      );
    },
    
    // ✅ UPDATE: Use visible statuses for filtering
    filteredAthletes() {
      if (!this.clubPersonRel) return [];
      
      return this.clubPersonRel
        .map(item => ({
          ...item,
          person: {
            ...item.person,
            fullName: `${(item.person && item.person.firstName) || ''} ${(item.person && item.person.lastName) || ''}`.trim()
          }
        }))
        .filter(athlete => {
          // Event filter
          if (this.selectedEvent.length > 0) {
            const eventId = athlete.event && athlete.event.eventId;
            if (!eventId || !this.selectedEvent.includes(eventId)) {
              return false;
            }
          }
          
          // Search filter
          if (this.search) {
            const searchLower = this.search.toLowerCase();
            const fullName = (athlete.person && athlete.person.fullName) || "";
            const email = (athlete.person && athlete.person.email) || "";
            const phone = (athlete.person && athlete.person.phone) || "";
            
            if (!fullName.toLowerCase().includes(searchLower) && 
                !email.toLowerCase().includes(searchLower) && 
                !phone.toLowerCase().includes(searchLower)) {
              return false;
            }
          }
          
          // Status filter from parent (if any)
          if (this.statusSelected.length > 0) {
            if (!this.statusSelected.includes(athlete.status)) {
              return false;
            }
          }
          
          // ✅ UPDATE: Only show statuses that are visible (not hidden)
          const visibleStatuses = this.visibleKanbanStatuses.map(s => s.value);
          return visibleStatuses.includes(athlete.status);
        });
    },
    
    // ✅ ADD: Class for view mode
    viewModeClass() {
      return this.viewMode === 'compact' ? 'view-compact' : 'view-comfortable';
    },
    
    // ✅ ADD: Group list for PersonDialog
    groupListForDialog() {
      return this.kanbanStatuses.map(status => ({
        node: {
          teamId: status.value,
          teamName: status.text
        }
      }));
    },
    
    // ✅ ADD: Get unique events for swimlanes
    swimlaneEvents() {
      if (!this.filteredAthletes.length) return [];
      
      const eventMap = new Map();
      
      this.filteredAthletes.forEach(athlete => {
        if (athlete.event && athlete.event.eventId) {
          eventMap.set(athlete.event.eventId, {
            eventId: athlete.event.eventId,
            name: athlete.event.name || `Event ${athlete.event.eventId}`,
            color: this.getEventColor(athlete.event.eventId)
          });
        } else {
          // Handle athletes without events
          eventMap.set('no-event', {
            eventId: 'no-event',
            name: 'No Event Assigned',
            color: 'grey'
          });
        }
      });
      
      return Array.from(eventMap.values()).sort((a, b) => {
        if (a.eventId === 'no-event') return 1; // Put "No Event" at the end
        if (b.eventId === 'no-event') return -1;
        return a.name.localeCompare(b.name);
      });
    },
    
    // ✅ ADD: Filtered events for swimlanes
    filteredSwimlaneEvents() {
      if (!this.selectedSwimlaneEvent) {
        return this.swimlaneEvents;
      }
      return this.swimlaneEvents.filter(event => 
        event.eventId === this.selectedSwimlaneEvent
      );
    },
    
    // ✅ ADD: Athletes grouped by event and status
    athletesByEventAndStatus() {
      const grouped = {};
      
      this.filteredAthletes.forEach(athlete => {
        const eventId = (athlete.event && athlete.event.eventId) || 'no-event';
        const status = athlete.status;
        
        if (!grouped[eventId]) {
          grouped[eventId] = {};
        }
        if (!grouped[eventId][status]) {
          grouped[eventId][status] = [];
        }
        
        grouped[eventId][status].push(athlete);
      });
      
      return grouped;
    },
  },
  
  // ✅ ADD: Watch for category changes
  watch: {
    selectedEvent: {
      handler() {
        console.log('Event filter changed:', this.selectedEvent);
      },
      immediate: true
    },
    
    search: {
      handler() {
        console.log('Search changed:', this.search);
      },
      immediate: true
    },
    
    kanbanStatuses: {
      handler(newStatuses) {
        console.log('Kanban statuses loaded:', newStatuses);
      },
      immediate: true
    },
    
    // ✅ ADD: Watch for status order changes
    statusOrderList: {
      handler(newOrder) {
        console.log('Status order changed:', newOrder);
      },
      deep: true
    },
    
    // ✅ ADD: Watch for order changes
    customStatusOrder: {
      handler(newOrder, oldOrder) {
        console.log('Custom status order changed:', {
          old: oldOrder,
          new: newOrder
        });
      },
      immediate: true
    },
  dialog: {
    handler(val) {
      if (!val) {
        this.close();
      } else if (this.editedIndex === -1) {
        this.editedItem.status = this.kanbanStatuses[0].value || "active";
        this.editedItem.clubId = this.$route.params.club;
      }
    },
    immediate: false
  },
  
  orderedKanbanStatuses: {
    handler(newStatuses) {
      console.log('Ordered kanban statuses updated:', newStatuses.map(s => s.value));
    },
    immediate: true
  },
  },
  
created() {
  this.loadData();
  this.loadStatusOrder();
  this.loadColumnVisibility(); // ✅ ADD: Load column visibility
},
  
  methods: {
    // ✅ All your existing methods stay the same...
    ...mapActions([
      "allClubPersonRel",
      "clubPersonRelMerge",
    ]),
    
    formatDatetime,
    getCommunicationCount,
    getLatestCommunication,
    
    async loadData() {
      this.isLoading = true;
      try {
        const clubId = this.$route && this.$route.params && this.$route.params.club;
        
        if (!clubId) {
          console.error('No club ID found in route');
          return;
        }
        
        await this.allClubPersonRel({ clubId });
        
        this.initializeStatusOrder();
        this.loadColumnVisibility(); // ✅ ADD: Load visibility settings
      } catch (error) {
        console.error("Failed to load athletes:", error);
      } finally {
        this.isLoading = false;
      }
    },
    
    refreshData() {
      this.loadData();
    },
    
    getAthletesByStatus(status) {
      let athletes = this.filteredAthletes.filter(athlete => athlete.status === status);
      
      // ✅ ADD: Additional filter for swimlane event selection
      if (this.showSwimlanes && this.selectedSwimlaneEvent) {
        athletes = athletes.filter(athlete => {
          const eventId = (athlete.event && athlete.event.eventId) || 'no-event';
          return eventId === this.selectedSwimlaneEvent;
        });
      }
      
      return athletes;
    },
    
    onDragStart(event, athlete) {
      this.draggedAthlete = athlete;
      event.dataTransfer.setData("text/plain", "");
    },
    
    sendEmail(email) {
      if (!email || email.trim() === '') {
        alert('No email address available');
        return;
      }
      window.open(`mailto:${email}`, '_self');
    },
    
    callPhone(phone) {
      if (!phone || phone.trim() === '') {
        alert('No phone number available');
        return;
      }
      const cleanPhone = phone.replace(/[^\d+]/g, '');
      window.open(`tel:${cleanPhone}`, '_self');
    },
    
    showCommunications(athlete) {
      // ✅ FIX: Vue 2 compatible null checks
      if (!athlete || !athlete.person) {
        console.error('Invalid athlete data');
        return;
      }
      this.selectedAthlete = athlete;
      this.communicationsDialog = true;
    },
    
    // ✅ FIX: Better null handling in drag operations
    async onDrop(event, newStatus) {
      event.preventDefault();
      
      // ✅ FIX: Vue 2 compatible null checks
      if (!this.draggedAthlete || 
          !this.draggedAthlete.person || 
          this.draggedAthlete.status === newStatus) {
        this.draggedAthlete = null;
        return;
      }
      
      // Confirm status change
      const athleteName = (this.draggedAthlete.person && this.draggedAthlete.person.fullName) || 'Unknown Athlete';
      //const confirmMessage = `Move ${athleteName} from ${this.draggedAthlete.status} to ${newStatus}?`;
      //if (!confirm(confirmMessage)) {
      //  this.draggedAthlete = null;
      //  return;
     // }
      
      try {
        // Update athlete status
        const updatedAthlete = {
          ...this.draggedAthlete,
          status: newStatus,
          updateSource: this.user && this.user.userName || 'Unknown User',
          updateDate: new Date().toISOString(),
        };
        
        await this.clubPersonRelMerge(updatedAthlete);
        
        // Add status change communication
        this.addStatusChangeComm(updatedAthlete, this.draggedAthlete.status, newStatus);
        
        // Refresh data
        await this.loadData();
        
        // ✅ FIX: Check if $toast exists before using it
        if (this.$toast) {
          this.$toast.success(`${athleteName} moved to ${newStatus}`);
        } else {
          console.log(`${athleteName} moved to ${newStatus}`);
        }
      } catch (error) {
        console.error("Failed to update athlete status:", error);
        // ✅ FIX: Check if $toast exists before using it
        if (this.$toast) {
          this.$toast.error("Failed to update athlete status");
        } else {
          alert("Failed to update athlete status");
        }
      }
      
      this.draggedAthlete = null;
    },
    
    addStatusChangeComm(athlete, oldStatus, newStatus) {
      // Add a communication entry for the status change
      let history = [];
      
      if (athlete.additionalInfo) {
        try {
          const parsed = JSON.parse(athlete.additionalInfo);
          if (Array.isArray(parsed)) {
            history = parsed;
          }
        } catch (e) {
          // Legacy format, convert to new format
          if (athlete.additionalInfo.trim()) {
            history = [{
              username: 'Legacy Entry',
              date: athlete.updateDate || new Date().toISOString(),
              message: athlete.additionalInfo
            }];
          }
        }
      }
      
      const statusChangeEntry = {
        username: (this.user && this.user.userName) || 'System',
        date: new Date().toISOString(),
        message: `Status changed from ${oldStatus} to ${newStatus} via kanban board`
      };
      
      history.unshift(statusChangeEntry);
      athlete.additionalInfo = JSON.stringify(history);
    },
    
    getCardClass(athlete) {
      const daysSinceUpdate = this.getDaysFromUpdate(athlete);
      
      if (daysSinceUpdate > 30) return "card-old";
      if (daysSinceUpdate > 14) return "card-stale";
      return "card-fresh";
    },
    
    getDaysFromUpdate(athlete) {
      if (!athlete.updateDate) return 0;
      
      const updateDate = new Date(athlete.updateDate);
      const now = new Date();
      return Math.floor((now - updateDate) / (1000 * 60 * 60 * 24));
    },
    
    getAgeColor(athlete) {
      const days = this.getDaysFromUpdate(athlete);
      if (days > 30) return "error";
      if (days > 14) return "warning";
      if (days > 7) return "orange";
      return "success";
    },
    
    openAthleteDetails(athlete) {
      // Current: Opens personDialogVisible (which shouldn't exist)
      // Should: Open the main dialog for editing
      this.editedIndex = 1;
      this.editedItem = { 
        ...athlete, 
        clubId: this.$route.params.club 
      };
      this.dialog = true;
    },
    
    editAthlete(athlete) {
      // Open edit dialog - integrate with your existing edit functionality
      this.openAthleteDetails(athlete);
    },
    
    openAddAthleteDialog() {
      // Integrate with your existing add athlete functionality
      this.editedIndex = -1;
      this.editedItem = { 
        ...this.defaultItem,
        clubId: this.$route.params.club,
        status: this.kanbanStatuses[0].value || "active"
      };
      this.dialog = true;
    },
    
    // ✅ ADD: Initialize status order based on current kanban statuses
    initializeStatusOrder() {
      this.statusOrderList = this.kanbanStatuses.map(status => ({
        value: status.value,
        text: status.text,
        color: status.color,
        icon: status.icon
      }));
    },
    
    // ✅ FIX: Save status order and apply it immediately
    saveStatusOrder() {
      // Extract the order from statusOrderList
      const newOrder = this.statusOrderList.map(status => status.value);
      
      // Update the custom order
      this.customStatusOrder = newOrder;
      
      // Save to localStorage
      const clubId = this.$route && this.$route.params && this.$route.params.club;
      if (clubId) {
        localStorage.setItem(
          `kanban-status-order-${clubId}`, 
          JSON.stringify(newOrder)
        );
      }
      
      this.showStatusOrderDialog = false;
      
      console.log('Status order saved:', newOrder);
      console.log('Updated custom order:', this.customStatusOrder);
      
      // ✅ FIX: Force reactivity update
      this.$forceUpdate();
      
      if (this.$toast) {
        this.$toast.success("Column order updated successfully!");
      } else {
        alert("Column order updated successfully!");
      }
    },
    
    // ✅ FIX: Reset status order
    resetStatusOrder() {
      this.customStatusOrder = [];
      this.hiddenColumns = []; // ✅ ADD: Also reset hidden columns
    
      const clubId = this.$route && this.$route.params && this.$route.params.club;
      if (clubId) {
        localStorage.removeItem(`kanban-status-order-${clubId}`);
        localStorage.removeItem(`kanban-hidden-columns-${clubId}`); // ✅ ADD
      }
      
      // Reset the dialog list to default order
      this.statusOrderList = [...this.orderedKanbanStatuses];
      
      // ✅ FIX: Force reactivity update
      this.$forceUpdate();
      
      console.log('Status order and visibility reset to default');
    },
    
    // ✅ FIX: Load status order
    loadStatusOrder() {
      const clubId = this.$route && this.$route.params && this.$route.params.club;
      if (!clubId) return;
      
      const saved = localStorage.getItem(`kanban-status-order-${clubId}`);
      if (saved) {
        try {
          this.customStatusOrder = JSON.parse(saved);
          console.log('Loaded custom status order:', this.customStatusOrder);
        } catch (e) {
          console.warn('Failed to load saved status order:', e);
          this.customStatusOrder = [];
        }
      }
    },
    
    // ✅ FIX: Initialize status order list for dialog
    openStatusOrderDialog() {
      // Use the current ordered statuses
      this.statusOrderList = [...this.orderedKanbanStatuses];
      this.showStatusOrderDialog = true;
    },
    
    // ✅ ADD: Method to handle drag end in dialog
    onStatusOrderDragEnd() {
      // This will be called when dragging ends in the dialog
      console.log('Drag ended, new order:', this.statusOrderList.map(s => s.value));
    },
    
    // ✅ ADD: Hide a column
    hideColumn(statusValue) {
      if (!this.hiddenColumns.includes(statusValue)) {
        this.hiddenColumns.push(statusValue);
        this.saveColumnVisibility();
      }
    },
    
    // ✅ ADD: Show a column
    showColumn(statusValue) {
      const index = this.hiddenColumns.indexOf(statusValue);
      if (index > -1) {
        this.hiddenColumns.splice(index, 1);
        this.saveColumnVisibility();
      }
    },
    
    // ✅ ADD: Toggle column visibility
    toggleColumnVisibility(statusValue) {
      if (this.hiddenColumns.includes(statusValue)) {
        this.showColumn(statusValue);
      } else {
        this.hideColumn(statusValue);
      }
    },
    
    // ✅ ADD: Show all columns
    showAllColumns() {
      this.hiddenColumns = [];
      this.saveColumnVisibility();
    },
    
    // ✅ ADD: Hide empty columns
    hideEmptyColumns() {
      const emptyColumns = this.orderedKanbanStatuses
        .filter(status => this.getAthletesByStatus(status.value).length === 0)
        .map(status => status.value);
    
      this.hiddenColumns = [...new Set([...this.hiddenColumns, ...emptyColumns])];
      this.saveColumnVisibility();
    },
    
    // ✅ ADD: Save column visibility to localStorage
    saveColumnVisibility() {
      const clubId = this.$route && this.$route.params && this.$route.params.club;
      if (clubId) {
        localStorage.setItem(
          `kanban-hidden-columns-${clubId}`, 
          JSON.stringify(this.hiddenColumns)
        );
      }
      console.log('Hidden columns saved:', this.hiddenColumns);
    },
    
    // ✅ ADD: Load column visibility from localStorage
    loadColumnVisibility() {
      const clubId = this.$route && this.$route.params && this.$route.params.club;
      if (!clubId) return;
    
      const saved = localStorage.getItem(`kanban-hidden-columns-${clubId}`);
      if (saved) {
        try {
          this.hiddenColumns = JSON.parse(saved);
          console.log('Loaded hidden columns:', this.hiddenColumns);
        } catch (e) {
          console.warn('Failed to load hidden columns:', e);
          this.hiddenColumns = [];
        }
      }
    },

    close() {
      this.dialog = false;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    },
    
    async saveAthlete(editedItem) {
      try {
        await this.clubPersonRelMerge(editedItem);
        await this.loadData();
        
        if (this.$toast) {
          this.$toast.success(
            this.editedIndex === -1 
              ? "Athlete added successfully!" 
              : "Athlete updated successfully!"
          );
        }
        
        this.close();
      } catch (error) {
        console.error("Failed to save athlete:", error);
        if (this.$toast) {
          this.$toast.error("Failed to save athlete");
        } else {
          alert("Failed to save athlete");
        }
      }
    },

    // ✅ ADD: Toggle swimlanes
    toggleSwimlanes() {
      this.showSwimlanes = !this.showSwimlanes;
      
      // Clear event filter when turning off swimlanes
      if (!this.showSwimlanes) {
        this.selectedSwimlaneEvent = null;
      }
    },
    
    // ✅ ADD: Get athletes by event and status
    getAthletesByEventAndStatus(eventId, status) {
      return (this.athletesByEventAndStatus[eventId] && 
              this.athletesByEventAndStatus[eventId][status]) || [];
    },
    
    // ✅ ADD: Get event color (you can customize this)
    getEventColor(eventId) {
      const colors = ['blue', 'green', 'orange', 'purple', 'teal', 'indigo', 'pink', 'red'];
      const hash = eventId.toString().split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      return colors[Math.abs(hash) % colors.length];
    },
    
    // ✅ ADD: Get total athletes in swimlane
    getTotalAthletesInSwimlane(eventId) {
      const eventAthletes = this.athletesByEventAndStatus[eventId] || {};
      return Object.values(eventAthletes).reduce((total, athletes) => total + athletes.length, 0);
    },
    
    // ✅ ADD: Enhanced drag feedback
    onDragEnter(event) {
      event.target.closest('.swimlane-drop-zone').classList.add('drag-over');
    },
    
    onDragLeave(event) {
      // Only remove if we're actually leaving the drop zone
      if (!event.currentTarget.contains(event.relatedTarget)) {
        event.target.closest('.swimlane-drop-zone').classList.remove('drag-over');
      }
    },
    
    // ✅ ADD: Focus on specific event
    focusOnEvent(eventId) {
      this.selectedSwimlaneEvent = eventId;
      
      if (this.$toast) {
        const event = this.swimlaneEvents.find(e => e.eventId === eventId);
        this.$toast.info(`Focusing on ${event ? event.name : 'Selected Event'}`);
      }
    },
    
    // ✅ ADD: Export event data
    exportEventData(eventId) {
      const event = this.swimlaneEvents.find(e => e.eventId === eventId);
      const eventAthletes = this.filteredAthletes.filter(athlete => 
        (athlete.event && athlete.event.eventId) === eventId
      );
      
      console.log(`Export data for ${event ? event.name : 'Event'}:`, eventAthletes);
      
      // You can implement actual export functionality here
      if (this.$toast) {
        this.$toast.info(`Exporting ${eventAthletes.length} athletes from ${event ? event.name : 'event'}`);
      }
    },
  },
};
</script>

<style scoped>
/* ✅ FIX: Kanban board container */
.kanban-container {
  height: calc(100vh - 280px); /* Adjust for header height */
  overflow: hidden;
  position: relative;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
}

.kanban-board {
  display: flex;
  gap: 0; /* Remove gap, use margin on columns instead */
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0;
  height: 100%;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: #ccc #f5f5f5;
}

/* ✅ FIX: Scrollbar styling */
.kanban-board::-webkit-scrollbar {
  height: 10px;
}

.kanban-board::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 5px;
}

.kanban-board::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 5px;
}

.kanban-board::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* ✅ FIX: Responsive column sizing */
.kanban-column {
  flex: 0 0 auto;
  width: 320px;
  display: flex;
  flex-direction: column;
  margin-right: 16px; /* Add consistent spacing between columns */
  background: transparent;
}

.view-compact .kanban-column {
  width: 280px;
  margin-right: 12px;
}

/* ✅ FIX: Column headers */
.kanban-column-header {
  position: sticky;
  top: 0;
  z-index: 3;
  margin-bottom: 12px;
  flex-shrink: 0;
  background: white;
  border-radius: 8px !important;
}

.kanban-column-header .v-card__title {
  padding: 12px 16px !important;
}

.view-compact .kanban-column-header .v-card__title {
  padding: 8px 12px !important;
}

/* ✅ FIX: Status title styling */
.status-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.view-compact .status-title {
  max-width: 120px;
  font-size: 14px !important;
}

/* ✅ FIX: Column content area */
.kanban-column-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 4px 16px 4px; /* Better padding */
  border: 2px dashed transparent;
  border-radius: 8px;
  transition: border-color 0.2s;
  min-height: 200px;
}

/* ✅ FIX: Athlete cards */
.athlete-card {
  margin-bottom: 12px;
  cursor: grab;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  z-index: 1;
}

.view-compact .athlete-card {
  margin-bottom: 8px;
}

.athlete-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.athlete-card:active {
  cursor: grabbing;
}

/* ✅ FIX: Empty column styling */
.empty-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 150px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  margin: 8px;
  background: #fafafa;
  color: #999;
}

.view-compact .empty-column {
  height: 100px;
  margin: 4px;
}

/* ✅ FIX: Compact mode athlete names */
.athlete-name-compact {
  font-size: 12px !important;
  font-weight: 600;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.view-compact .athlete-name-compact {
  font-size: 11px !important;
  max-width: 120px;
}

/* ✅ FIX: Compact info text */
.compact-info {
  font-size: 10px !important;
  line-height: 1.2;
  color: #666;
  margin-top: 4px;
}

.text-caption-compact {
  font-size: 10px !important;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  max-width: 160px;
  color: #777;
}

/* ✅ FIX: Chips */
.compact-chip {
  font-size: 9px !important;
  height: 18px !important;
  min-width: 30px !important;
  border-radius: 9px !important;
}

.compact-menu-btn {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
}

.compact-icon {
  font-size: 14px !important;
}

/* ✅ ADD: Swimlane-specific styles */
.swimlane-container {
  height: calc(100vh - 280px);
  overflow: hidden;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

/* ✅ STICKY STATUS HEADERS */
.swimlane-sticky-header {
  display: flex;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: #f8f9fa;
  padding-bottom: 12px;
  margin-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.swimlane-sticky-column {
  flex: 1;
  min-width: 160px;
}

.sticky-status-header {
  background: white !important;
  border: 2px solid #e0e0e0 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
}

.sticky-status-header .status-title {
  font-size: 13px !important;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ SCROLLABLE CONTENT AREA */
.swimlane-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

/* ✅ EVENT ROWS */
.swimlane-event-row {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* ✅ EVENT BANNER (spans full width) */
.event-header-row {
  width: 100%;
}

.event-banner {
  width: 100%;
  border-radius: 8px 8px 0 0 !important;
}

.event-title {
  font-size: 14px !important;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ STATUS COLUMNS (aligned with sticky headers) */
.status-columns-row {
  display: flex;
  gap: 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 8px;
}

.swimlane-status-column {
  flex: 1;
  min-width: 160px;
}

/* ✅ DROP ZONES */
.swimlane-drop-zone {
  min-height: 60px;
  padding: 6px;
  border: 2px dashed transparent;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.swimlane-drop-zone:hover {
  border-color: #e0e0e0;
  background-color: #f8f9fa;
}

.swimlane-drop-zone.drag-over {
  border-color: #2196f3 !important;
  background-color: rgba(33, 150, 243, 0.1) !important;
  transform: scale(1.02);
}

/* ✅ ATHLETE CARDS */
.swimlane-athlete-card {
  margin-bottom: 8px; /* Same as compact view */
  cursor: grab;
  transition: all 0.2s ease;
}

.swimlane-athlete-card:last-child {
  margin-bottom: 0;
}

.swimlane-athlete-card:hover {
  transform: translateY(-2px); /* Same as regular compact cards */
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.swimlane-athlete-card:active {
  cursor: grabbing;
  opacity: 0.7; /* Same as regular drag feedback */
}

/* ✅ UPDATE: Use same card styling */
.swimlane-athlete-card .compact-athlete-card {
  border-radius: 8px !important; /* Same as regular cards */
  border-left: 3px solid transparent;
}

/* ✅ UPDATE: Swimlane cards use exact same athlete name styling */
.swimlane-athlete-card .athlete-name-compact {
  font-size: 12px !important; /* Same as compact view */
  font-weight: 600;
  max-width: 140px; /* Same as compact view */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

/* ✅ UPDATE: Same compact info styling */
.swimlane-athlete-card .compact-info {
  font-size: 10px !important;
  line-height: 1.2;
  color: #666;
  margin-top: 4px;
}

.swimlane-athlete-card .text-caption-compact {
  font-size: 10px !important;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  max-width: 160px; /* Same as compact view */
  color: #777;
}

/* ✅ UPDATE: Same chip styling */
.swimlane-athlete-card .compact-chip {
  font-size: 9px !important;
  height: 18px !important;
  min-width: 30px !important;
  border-radius: 9px !important;
}

.swimlane-athlete-card .compact-menu-btn {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
}

.swimlane-athlete-card .compact-icon {
  font-size: 14px !important;
}

/* ✅ UPDATE: Same card aging indicators */
.swimlane-athlete-card .card-fresh .compact-athlete-card {
  border-left-color: #4caf50 !important;
}

.swimlane-athlete-card .card-stale .compact-athlete-card {
  border-left-color: #ff9800 !important;
}

.swimlane-athlete-card .card-old .compact-athlete-card {
  border-left-color: #f44336 !important;
  opacity: 0.9;
}

/* ✅ UPDATE: Same card text padding */
.swimlane-athlete-card .v-card__text {
  padding: 8px !important; /* Same as compact view */
}

/* ✅ UPDATE: Responsive adjustments to match compact view */
@media (max-width: 1200px) {
  .swimlane-sticky-column,
  .swimlane-status-column {
    min-width: 140px;
  }
  
  .swimlane-athlete-card .athlete-name-compact {
    font-size: 11px !important; /* Same as compact responsive */
    max-width: 120px;
  }
}

@media (max-width: 992px) {
  .swimlane-sticky-column,
  .swimlane-status-column {
    min-width: 120px;
  }
  
  .swimlane-athlete-card .athlete-name-compact {
    font-size: 10px !important;
    max-width: 100px;
  }
  
  .swimlane-athlete-card .text-caption-compact {
    font-size: 9px !important;
    max-width: 120px;
  }
}

@media (max-width: 768px) {
  .swimlane-sticky-column,
  .swimlane-status-column {
    min-width: 100px;
  }
  
  .swimlane-athlete-card .athlete-name-compact {
    font-size: 9px !important;
    max-width: 80px;
  }
  
  .swimlane-athlete-card .compact-info {
    font-size: 8px !important;
  }
}

/* ✅ ADD: Swimlane responsive sizing based on view mode */
.view-compact .swimlane-sticky-column,
.view-compact .swimlane-status-column {
  min-width: 140px;
}

.view-comfortable .swimlane-sticky-column,
.view-comfortable .swimlane-status-column {
  min-width: 200px;
}

/* ✅ ADD: Swimlane drop zone heights based on view mode */
.view-compact .swimlane-drop-zone {
  min-height: 50px;
}

.view-comfortable .swimlane-drop-zone {
  min-height: 80px;
}

/* ✅ ADD: Event title responsive to view mode */
.view-compact .event-title {
  font-size: 12px !important;
  max-width: 150px;
}

.view-comfortable .event-title {
  font-size: 14px !important;
  max-width: 200px;
}

/* ✅ ADD: Responsive swimlane containers */
@media (max-width: 1200px) {
  .view-compact .swimlane-sticky-column,
  .view-compact .swimlane-status-column {
    min-width: 120px;
  }
  
  .view-comfortable .swimlane-sticky-column,
  .view-comfortable .swimlane-status-column {
    min-width: 180px;
  }
}

@media (max-width: 992px) {
  .view-compact .swimlane-sticky-column,
  .view-compact .swimlane-status-column {
    min-width: 100px;
  }
  
  .view-comfortable .swimlane-sticky-column,
  .view-comfortable .swimlane-status-column {
    min-width: 160px;
  }
}
</style>